/**
 * Renderer property-binding shortcuts — shared editor utilities.
 *
 * These wrap the multi-step "bind a Properties declaration to a Renderer
 * field" flows so every editor (trace-sandbox, TraceFrontend) wires bindings
 * the same way instead of hand-rolling `$name` patches:
 *
 * - `bindRendererProperty()` — point an existing property at a Renderer field
 *   (`materials[slot].baseColorFactor`, `materials[slot].src`, `opacity`).
 * - `bindMaterialToProperty()` — full shortcut: ensure a Properties behavior,
 *   add a `kind: 'material'` string declaration seeded from the current
 *   material, and bind it to `materials[slot].src` — one undo entry.
 *
 * Both take SceneObject refs (never name strings) and record undo via the
 * shared UndoManager unless `record: false`, in which case the built command
 * is returned for composition into a caller-owned CompositeCommand.
 */

import type { SceneObject } from '../core/SceneObject';
import type { ValidationError } from '../scenedata/behaviordata';
import type { MaterialData, RendererData } from '../scenedata/behaviors/rendererdata';
import type { PropertiesData, PropertyDeclaration } from '../scenedata/behaviors/propertiesdata';
import { boundPropertyName, isPropertyBinding, wholeRefName, WHOLE_REF_RE } from '../behaviors/tracebehavior';
import { Renderer } from '../behaviors/renderer';
import { Properties, type PropertyValue } from '../behaviors/properties';
import type { UndoCommand } from './undo/UndoCommand';
import { getUndoManager } from './undo/UndoManager';
import { BehaviorCommitCommand } from './undo/BehaviorCommitCommand';
import { AddBehaviorCommand } from './undo/AddBehaviorCommand';
import { CompositeCommand } from './undo/CompositeCommand';
import { dispatchBehaviorChanged } from './undo/editor-events';
import { MaterialPreviews } from './MaterialPreviews';
import { freezeStatesForDeclaration, ensureDeclaration, setDeclarationValue } from './states/statesModel';
import { capturePageState, type PageEditing } from './states/pageCapture';
import { pageStateRefusal, pageStateRefusalIfAsked, routeOf, type Route } from './authoring/editRoute';
import { editTargetState, captureStateValue, stateEditRefused } from './states/statesActions';
import { findPropertyProvider } from './property-provider';
import { declarationForValue, valueMatchesType } from './style-sheets';
import { THEME_DECLARATION_INHERITS, THEME_PROPERTY_SHAPES } from '../scenedata/theme-vocabulary';
import { Logger } from '../utils/Logger';
import { materialOwnFinish } from '../utils/factorTexture';

/** The Renderer field and binding target of each numeric value a slot overrides: its PBR factors and its own opacity. */
const FACTORS = {
    roughness: { field: 'roughnessFactor', binding: 'materialRoughness' },
    metallic: { field: 'metallicFactor', binding: 'materialMetallic' },
    opacity: { field: 'opacity', binding: 'materialOpacity' },
} as const;

type Factor = keyof typeof FACTORS;

/**
 * The declaration a Renderer field is bound to, shaped by its binding target.
 * It is the edited object's own (`inherits: false`), so an ancestor declaring
 * the same name never shadows it.
 */
export function bindingDeclaration(
    binding: RendererBindingTarget['binding'],
    name: string,
    value: PropertyValue,
): PropertyDeclaration & { value: PropertyValue } {
    const own = { name, inherits: THEME_DECLARATION_INHERITS };
    switch (binding) {
        case 'materialSrc':
            return { ...own, type: 'string', kind: 'material', value: String(value) };
        case 'materialColor':
        case 'tint':
            return { ...own, type: 'color', value: String(value) };
        case 'opacity':
            return { ...own, type: 'number', value: Number(value), min: 0, max: 1, step: 0.05 };
        default:
            return { ...own, type: 'number', value: Number(value), min: 0, max: 1, step: 0.01 };
    }
}

function unit(value: number): number {
    return Math.min(1, Math.max(0, value));
}

/** A factor write that changed nothing, with the errors that say why, if any. */
function nothingWritten(errors: ValidationError[] = []): SetMaterialForStateResult {
    return { errors, target: null, propertyName: null, command: null, autoBound: false };
}

/** #rrggbb or #rrggbbaa. */
function isHexColor(v: unknown): v is string {
    return typeof v === 'string' && /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(v);
}

/** Renderer fields a property can be bound to. */
export type RendererBindingTarget =
    | { binding: 'materialColor' | 'materialSrc'; slot?: number }
    // PBR factors and the slot's own opacity. Authored data holds the binding
    // string; the resolved value is numeric — the same shape `opacity` uses.
    | { binding: 'materialRoughness' | 'materialMetallic' | 'materialOpacity'; slot?: number }
    | { binding: 'opacity' }
    // Whole-model tint: multiplies into every material's authored base color
    // across all slots (GLB-authored textured materials included), unlike
    // materialColor which replaces one slot's color.
    | { binding: 'tint' };

export interface BindCommandOptions {
    /**
     * Record the operation on the shared UndoManager (default true).
     * Pass false to compose the returned command into a CompositeCommand.
     */
    record?: boolean;
    /**
     * Dispatch an `editor:behavior-changed` event for the write (default
     * true). Pass false when composing many binds into one action — the
     * composer announces once at the end (and not at all if it fails and
     * rolls back), instead of a per-bind burst describing intermediate
     * states.
     */
    notify?: boolean;
}

export interface BindRendererPropertyResult {
    /** Empty on success. */
    errors: ValidationError[];
    /** The undo command for the Renderer commit (null when the bind failed). */
    command: UndoCommand | null;
}

/**
 * Bind an existing property to a Renderer field by writing a `$name`
 * reference into the Renderer's authored data. The property's resolved value
 * (including state overrides and transitions) drives the field from then on.
 */
export async function bindRendererProperty(
    sceneObject: SceneObject,
    propertyName: string,
    target: RendererBindingTarget,
    options: BindCommandOptions = {},
): Promise<BindRendererPropertyResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return {
            errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }],
            command: null,
        };
    }

    const before = renderer.getData();
    const ref = `$${propertyName}`;

    let patch: Partial<RendererData>;
    if (target.binding === 'opacity') {
        // Authored data holds the binding string; the resolved value is numeric.
        patch = { opacity: ref as unknown as number };
    } else if (target.binding === 'tint') {
        patch = { tint: ref };
    } else {
        const numeric = Object.values(FACTORS).find(factor => factor.binding === target.binding);
        const slot = target.slot ?? 0;
        const materials: MaterialData[] = (before.materials ?? []).map(m => ({ ...m }));
        let entry = materials.find(m => (m.targetMaterialIndex ?? 0) === slot);
        if (!entry) {
            entry = { targetMaterialIndex: slot };
            materials.push(entry);
        }
        if (numeric) {
            // The numeric slot fields: typed `number`, carrying the binding string
            // as the whole-object `opacity` does.
            entry[numeric.field] = ref as unknown as number;
        } else {
            entry[target.binding === 'materialColor' ? 'baseColorFactor' : 'src'] = ref;
        }
        patch = { materials };
    }

    const errors = await renderer.commit(patch);
    if (errors.length > 0) {
        return { errors, command: null };
    }

    const command = new BehaviorCommitCommand(
        sceneObject, 'Renderer', before, renderer.getData(), null,
    );
    if (options.record !== false && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    if (options.notify !== false) {
        dispatchBehaviorChanged({
            objectPath: sceneObject.getPath(),
            behaviorType: 'Renderer',
            action: 'updated',
        });
    }
    return { errors: [], command };
}

export interface BindMaterialToPropertyOptions extends BindCommandOptions {
    /** Material slot (targetMaterialIndex) to bind. @default 0 */
    slot?: number;
    /** Property name to declare. De-duplicated against existing declarations. @default "material" */
    propertyName?: string;
    /** Display label for the declaration (editor UI / AutoUI). */
    label?: string;
}

export interface BindMaterialToPropertyResult {
    /** Empty on success. */
    errors: ValidationError[];
    /** The declared property name actually used (after de-duplication). */
    propertyName: string | null;
    /** Composite undo command covering all steps (null when the bind failed). */
    command: UndoCommand | null;
}

/**
 * Full "state-swappable material" shortcut:
 *
 * 1. Ensure the object has a Properties behavior (added if missing).
 * 2. Add a `{ type: 'string', kind: 'material' }` declaration, seeded with the
 *    slot's current material `src` (empty = native material).
 * 3. Bind `materials[slot].src` to the new property.
 *
 * All steps land as ONE undo entry. States on the Properties behavior can
 * then override the property per state to swap the material at runtime.
 */
export async function bindMaterialToProperty(
    sceneObject: SceneObject,
    options: BindMaterialToPropertyOptions = {},
): Promise<BindMaterialToPropertyResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return {
            errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }],
            propertyName: null,
            command: null,
        };
    }

    // Seeded from the slot's current src (empty = native material).
    const slot = options.slot ?? 0;
    const slotSrc = (renderer.getData().materials ?? [])
        .find(m => (m.targetMaterialIndex ?? 0) === slot)?.src;
    const declaration = {
        ...bindingDeclaration('materialSrc', options.propertyName ?? 'material', slotSrc && !isPropertyBinding(slotSrc) ? slotSrc : ''),
        ...(options.label ? { label: options.label } : {}),
    };
    return declareAndBind(sceneObject, declaration, { binding: 'materialSrc', slot }, {
        label: `Bind material to property on ${sceneObject.name}`,
        record: options.record !== false,
    });
}

export interface BindMaterialColorToPropertyOptions extends BindCommandOptions {
    /** Material slot (targetMaterialIndex) to bind. @default 0 */
    slot?: number;
    /** Property name to declare. De-duplicated against existing declarations. @default "color" */
    propertyName?: string;
    /** Display label for the declaration (editor UI / AutoUI). */
    label?: string;
    /** Fallback seed color (#rrggbb[aa]) when no authored/detectable color exists. */
    seedColor?: string;
    /** Freeze pre-existing states to the seed value (first-bind isolation). @default true */
    freeze?: boolean;
}

/**
 * Color analog of {@link bindMaterialToProperty}:
 *
 * 1. Ensure a Properties behavior.
 * 2. Add a `{ type: 'color' }` declaration seeded from the slot's current base
 *    color (authored literal `baseColorFactor` → `seedColor` → detected albedo),
 *    freezing pre-existing states to it.
 * 3. Bind `materials[slot].baseColorFactor` to the new property.
 *
 * One undo entry. States can then override the color per state.
 */
export async function bindMaterialColorToProperty(
    sceneObject: SceneObject,
    options: BindMaterialColorToPropertyOptions = {},
): Promise<BindMaterialToPropertyResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return {
            errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }],
            propertyName: null,
            command: null,
        };
    }

    const slot = options.slot ?? 0;
    // Seeded from the slot's current base color: the authored literal, else `seedColor`, else the detected albedo.
    const seed = colorSeed(sceneObject, slot, options.seedColor);
    const declaration = {
        ...bindingDeclaration('materialColor', options.propertyName ?? 'color', seed),
        ...(options.label ? { label: options.label } : {}),
    };
    // freeze=false (variant-set creation) declares WITHOUT the state sweep so
    // variant properties compose with states. The default freezes pre-existing
    // states — setMaterialForState's auto-bind relies on it.
    return declareAndBind(sceneObject, declaration, { binding: 'materialColor', slot }, {
        label: `Bind material color to property on ${sceneObject.name}`,
        record: options.record !== false,
        freeze: options.freeze !== false,
    });
}

/**
 * The shared tail of the `bind*ToProperty` shortcuts, as ONE undo entry:
 * ensure a Properties behavior, add `declaration` (its name de-duplicated
 * against existing declarations), freezing pre-existing states to its value
 * unless `freeze` is false (see freezeStatesForDeclaration: binding makes the
 * field state-swappable, so states authored before the bind would otherwise
 * follow Base retroactively), then bind `target` to it. A failed step takes
 * back the ones before it.
 */
async function declareAndBind(
    sceneObject: SceneObject,
    declaration: PropertyDeclaration & { value: PropertyValue },
    target: RendererBindingTarget,
    options: { label: string; record: boolean; freeze?: boolean },
): Promise<BindMaterialToPropertyResult> {
    const commands: UndoCommand[] = [];
    const rollback = async (): Promise<void> => {
        for (let i = commands.length - 1; i >= 0; i--) {
            await commands[i].undo();
        }
    };

    let properties = sceneObject.getBehavior(Properties);
    let propertiesAdded = false;
    if (!properties) {
        properties = await sceneObject.addBehavior(Properties);
        propertiesAdded = true;
        commands.push(new AddBehaviorCommand(sceneObject, 'Properties', properties.getData()));
    }

    const propsBefore = properties.getData();
    const existingNames = new Set((propsBefore.properties ?? []).map(d => d.name));
    let name = declaration.name;
    if (existingNames.has(name)) {
        let i = 2;
        while (existingNames.has(`${name}${i}`)) i++;
        name = `${name}${i}`;
    }
    const declared = { ...declaration, name };
    const frozen = options.freeze === false
        ? ensureDeclaration(propsBefore, declared)
        : freezeStatesForDeclaration(propsBefore, declared);
    const declErrors = await properties.commit({ properties: frozen.properties, states: frozen.states });
    if (declErrors.length > 0) {
        await rollback();
        return { errors: declErrors, propertyName: null, command: null };
    }
    commands.push(new BehaviorCommitCommand(
        sceneObject, 'Properties', propsBefore, properties.getData(), null,
    ));

    const bind = await bindRendererProperty(sceneObject, name, target, { record: false });
    if (bind.errors.length > 0 || !bind.command) {
        await rollback();
        return { errors: bind.errors, propertyName: null, command: null };
    }
    commands.push(bind.command);

    const command = new CompositeCommand(options.label, commands);
    if (options.record && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    // Second event on purpose: bindRendererProperty already announced the
    // Renderer change; this announces the Properties change. Subscribers are
    // keyed by behaviorType, so each UI region refreshes once.
    dispatchBehaviorChanged({
        objectPath: sceneObject.getPath(),
        behaviorType: 'Properties',
        action: propertiesAdded ? 'added' : 'updated',
    });
    return { errors: [], propertyName: name, command };
}

export interface ApplyMaterialSrcResult {
    /** Empty on success. */
    errors: ValidationError[];
    /**
     * Where the write landed: the active state override, the bound Properties
     * declaration (Base), or the Renderer's authored materials (unbound slot).
     */
    target: 'state' | 'property' | 'renderer' | null;
    /** The bound property name, when target is 'state' or 'property'. */
    propertyName: string | null;
    /** Undo command for the write (null on failure). */
    command: UndoCommand | null;
}

/**
 * Set a material slot's src WITHOUT destroying a property binding.
 *
 * Pickers historically wrote `materials[slot].src` directly; on a bound slot
 * (`src: "$material"`) that replaces the binding with a literal URL and
 * silently disconnects every state override. So the write is routed:
 *
 * - slot bound  → update the bound property's declaration value on the
 *   EDITED OBJECT's own Properties, declaring it there when only a sheet or
 *   an ancestor supplied it. Not the providing behavior — see
 *   {@link ownBindingHost} for why an ancestor walk repainted siblings.
 * - slot unbound → update the Renderer's authored src (legacy behavior)
 *
 * Pass `src: ''` on a bound slot to mean "native material".
 */
export async function applyMaterialSrc(
    sceneObject: SceneObject,
    src: string,
    options: { slot?: number } & BindCommandOptions = {},
): Promise<ApplyMaterialSrcResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return {
            errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }],
            target: null, propertyName: null, command: null,
        };
    }

    const slot = options.slot ?? 0;
    const record = options.record !== false;
    const authoredSrc = (renderer.getData().materials ?? [])
        .find(m => (m.targetMaterialIndex ?? 0) === slot)?.src;
    const refMatch = authoredSrc?.match(WHOLE_REF_RE);

    // ── Unbound slot: legacy direct write to the Renderer ──
    if (!refMatch) {
        const before = renderer.getData();
        const materials: MaterialData[] = (before.materials ?? []).map(m => ({ ...m }));
        let entry = materials.find(m => (m.targetMaterialIndex ?? 0) === slot);
        if (!entry) {
            entry = { targetMaterialIndex: slot };
            materials.push(entry);
        }
        entry.src = src;
        const errors = await renderer.commit({ materials });
        if (errors.length > 0) return { errors, target: null, propertyName: null, command: null };

        // fieldKey enables coalescing — rapid picker/typing edits merge into
        // one undo entry instead of one per keystroke.
        const command = new BehaviorCommitCommand(
            sceneObject, 'Renderer', before, renderer.getData(), `materials[${slot}].src`,
        );
        if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
        dispatchBehaviorChanged({
            objectPath: sceneObject.getPath(), behaviorType: 'Renderer', action: 'updated',
        });
        return { errors: [], target: 'renderer', propertyName: null, command };
    }

    // ── Bound slot: route into the active state, else the declaration base ──
    const name = wholeRefName(refMatch);

    // A non-Base state active on this object's group → author the override there
    // (picking a material while previewing a state authors that state). A
    // refused capture (Play, no host guard) writes nothing rather than Base.
    if (editTargetState(sceneObject) !== undefined) {
        const { captured, command } = await captureStateValue(sceneObject, name, src, { record });
        return { errors: [], target: captured ? 'state' : null, propertyName: name, command };
    }

    // Base: write the providing declaration's base value, declaring it inline
    // when the style supplied it from a SHEET (or when nothing declares it at
    // all). See {@link authorBoundValue} for why that is the right answer
    // rather than an error.
    return authorBoundValue(sceneObject, name, 'string', src, record, `Set material on ${sceneObject.name}`);
}

/**
 * Set a material slot's base color (`baseColorFactor`) WITHOUT destroying a
 * property binding — the color analog of {@link applyMaterialSrc}. Routes:
 *
 * - slot bound + non-Base state active → the active state's override
 * - slot bound + Base                  → the providing declaration's base value
 * - slot unbound                       → the Renderer's authored baseColorFactor
 *
 * `hex` is `#rrggbb` or `#rrggbbaa`. Editors must call this from color pickers
 * instead of committing `materials` directly — a literal write clobbers a
 * `{color}` binding and disconnects every state override.
 */
export async function applyMaterialColor(
    sceneObject: SceneObject,
    hex: string,
    options: { slot?: number } & BindCommandOptions = {},
): Promise<ApplyMaterialSrcResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return {
            errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }],
            target: null, propertyName: null, command: null,
        };
    }

    const slot = options.slot ?? 0;
    const record = options.record !== false;
    const authoredColor = (renderer.getData().materials ?? [])
        .find(m => (m.targetMaterialIndex ?? 0) === slot)?.baseColorFactor;
    const refMatch = typeof authoredColor === 'string' ? authoredColor.match(WHOLE_REF_RE) : null;

    // ── Unbound slot: legacy direct write to the Renderer ──
    if (!refMatch) {
        const before = renderer.getData();
        const materials: MaterialData[] = (before.materials ?? []).map(m => ({ ...m }));
        let entry = materials.find(m => (m.targetMaterialIndex ?? 0) === slot);
        if (!entry) {
            entry = { targetMaterialIndex: slot };
            materials.push(entry);
        }
        entry.baseColorFactor = hex;
        const errors = await renderer.commit({ materials });
        if (errors.length > 0) return { errors, target: null, propertyName: null, command: null };

        // fieldKey enables coalescing — color pickers stream events on drag.
        const command = new BehaviorCommitCommand(
            sceneObject, 'Renderer', before, renderer.getData(), `materials[${slot}].baseColorFactor`,
        );
        if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
        dispatchBehaviorChanged({
            objectPath: sceneObject.getPath(), behaviorType: 'Renderer', action: 'updated',
        });
        return { errors: [], target: 'renderer', propertyName: null, command };
    }

    // ── Bound slot: route into the active state, else the declaration base ──
    const name = wholeRefName(refMatch);

    if (editTargetState(sceneObject) !== undefined) {
        const { captured, command } = await captureStateValue(sceneObject, name, hex, { record });
        return { errors: [], target: captured ? 'state' : null, propertyName: name, command };
    }

    // Base: write the providing declaration's base value, declaring it inline
    // when the style supplied it from a sheet — see {@link authorBoundValue}.
    return authorBoundValue(sceneObject, name, 'color', hex, record, `Set colour on ${sceneObject.name}`);
}

/**
 * One material edit at a time, per object.
 *
 * These entry points are called WITHOUT awaiting during a colour drag, and the
 * body between reading `getData()` and committing a whole replacement
 * `properties` array is not re-entrant: two overlapping edits both snapshot the
 * same `before`, and the second commit erases the first's declaration while
 * both report success. Serializing the whole edit — not just the behavior
 * attach — is what makes every read-then-write inside it safe, and it is also
 * what stops two edits from both attaching a Properties, and what keeps one
 * edit's failure rollback from deleting a behavior another edit committed into.
 *
 * A queue, not a lock: nothing is dropped, order is preserved, and at drag
 * cadence (a pointermove every ~8 ms against a commit that resolves in
 * microtasks) the difference is invisible. A rejected predecessor does not
 * cancel its successor.
 */
const EDIT_QUEUE = new WeakMap<SceneObject, Promise<unknown>>();

/** Shared with `text-bindings.ts`; not part of the public barrel. */
export function queuePerObject<T>(sceneObject: SceneObject, run: () => Promise<T>): Promise<T> {
    const previous = EDIT_QUEUE.get(sceneObject) ?? Promise.resolve();
    const result = previous.then(run, run);
    // Stored swallowed: the queue only sequences, so a rejection here must not
    // surface as an unhandled rejection on the next edit's tail.
    EDIT_QUEUE.set(sceneObject, result.then(() => undefined, () => undefined));
    return result;
}

type BindingHost =
    | { ok: true; owner: SceneObject; properties: Properties; attach: UndoCommand | null }
    | { ok: false; error: ValidationError };

/**
 * The Properties this object's own material edits are written on.
 *
 * Deliberately NOT an ancestor walk. A material pick is a per-object,
 * per-slot gesture; the declaration it authors has to land on the object the
 * user is editing, or the edit is a theme-wide override wearing a per-object
 * costume. The provider for a styled slot is normally the themed ROOT (a
 * style lands on a root, and adoption writes bindings and nothing else), so
 * resolving the write target by provider repainted every sibling bound to
 * the same property — `style-actions.ts` already has a name for that
 * operation, `setThemeValues`, and this is not it.
 *
 * Paired with `inherits: false` on the declaration, writing here makes the
 * nearest scope win over both a group and the root.
 *
 * Callers run inside {@link queuePerObject}, so the check and the add cannot
 * interleave with another edit on the same object.
 */
async function ownBindingHost(
    sceneObject: SceneObject,
): Promise<BindingHost> {
    const own = sceneObject.getBehavior(Properties);
    if (own) return { ok: true, owner: sceneObject, properties: own, attach: null };

    let properties: Properties;
    try {
        properties = await sceneObject.addBehavior(Properties);
    } catch (err) {
        // This API returns errors; a caller mid-drag must not receive a throw
        // it never subscribed to.
        return {
            ok: false,
            error: {
                message: `Could not attach Properties to "${sceneObject.name}": ${err instanceof Error ? err.message : String(err)}`,
                code: 'NOT_AUTHORABLE',
            },
        };
    }
    return {
        ok: true,
        owner: sceneObject,
        properties,
        attach: new AddBehaviorCommand(sceneObject, 'Properties', properties.getData()),
    };
}

/**
 * Commit a declaration list onto its owning Properties behavior: the shared
 * tail of every "write the bound property's base value" path.
 */
async function commitDeclarations(
    owner: SceneObject,
    properties: Properties,
    name: string,
    before: PropertiesData,
    declarations: PropertyDeclaration[],
    record: boolean,
    attached = false,
    states?: PropertiesData['states'],
): Promise<ApplyMaterialSrcResult> {
    const errors = await properties.commit(
        states === undefined ? { properties: declarations } : { properties: declarations, states },
    );
    if (errors.length > 0) return { errors, target: null, propertyName: name, command: null };

    const command = new BehaviorCommitCommand(
        owner, 'Properties', before, properties.getData(), `property:${name}`,
    );
    if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    dispatchBehaviorChanged({
        objectPath: owner.getPath(), behaviorType: 'Properties',
        // A behavior that did not exist a moment ago is 'added'. Reporting it
        // as 'updated' tells a panel to refresh a row it has never drawn.
        action: attached ? 'added' : 'updated',
    });
    return { errors: [], target: 'property', propertyName: name, command };
}

/**
 * Write a bound property's base value, DECLARING it inline when nothing
 * authorable declares it yet.
 *
 * Applying a Style binds each slot (`$roleSrc` / `$roleColor`) and records
 * the sheet on the provider, so the style's values live in a LOADED SHEET,
 * never in the provider's inline `properties` array. The old lookup read
 * `getData().properties` — inline declarations only — so every later edit on
 * a styled object bailed as NOT_AUTHORABLE, silently: these functions return
 * errors, and the Studio discarded them and marked the slot applied anyway.
 *
 * Setting Style back to None did not recover: `clearStyleSheet` drops the
 * sheet but the Renderer keeps its binding (which is why the GLB's own
 * materials reappear), so the next pick failed as NO_PROVIDER instead.
 *
 * Authoring an inline declaration is the sanctioned answer, not a
 * workaround: inline beats sheets in `PropertyResolver.rebuildPropertyMap`,
 * so it is exactly the "user tweak overrides the style" override the model
 * already describes, and it un-sticks the post-None case too. The host is the
 * object being edited — see {@link ownBindingHost}.
 */
function authorBoundValue(
    sceneObject: SceneObject,
    name: string,
    type: 'string' | 'color' | 'number',
    value: string | number,
    record: boolean,
    label: string,
): Promise<ApplyMaterialSrcResult> {
    return queuePerObject(sceneObject, () =>
        authorBoundValueInner(sceneObject, name, type, value, record, label));
}

/** Shared with `text-bindings.ts`; not part of the public barrel. */
export async function authorBoundValueInner(
    sceneObject: SceneObject,
    name: string,
    type: 'string' | 'color' | 'number',
    value: string | number,
    record: boolean,
    label: string,
): Promise<ApplyMaterialSrcResult> {
    // A color binding may legitimately be declared as either 'color' or
    // 'string' (the manual bind button can point a string at a color field), so
    // an existing declaration of EITHER type is updated in place rather than
    // shadowed by a second one under the same name.
    const authorable = (d: PropertyDeclaration): boolean =>
        type === 'color' ? d.type === 'color' || d.type === 'string' : d.type === type;

    // What this property resolves to RIGHT NOW — a sheet's value, an ancestor
    // theme's, or nothing at all. Read before anything is written, and before
    // the host attach, because it is the value pre-existing states are already
    // rendering and the freeze below has to be seeded with it.
    const preEdit = sceneObject.resolveProperty(name);

    const host = await ownBindingHost(sceneObject);
    if (!host.ok) {
        return { errors: [host.error], target: null, propertyName: name, command: null };
    }

    const before = host.properties.getData();
    const existing = (before.properties ?? []).find(d => d.name === name);
    const replaced: PropertyDeclaration[] = (before.properties ?? []).map(d => {
        if (d.name !== name || !authorable(d)) return d;
        // Narrowed by `authorable`: the string/color arms take a string, the
        // number arm a number, and `value` matches `type` by construction.
        //
        // `inherits` is pinned on UPDATE as well as on create. A pre-existing
        // inline declaration that inherits is one resolution walks past to an
        // ancestor, so updating it would save the pick and render the theme's
        // value — the same silent no-op the create branch guards against, one
        // branch over.
        return { ...d, value, inherits: THEME_DECLARATION_INHERITS } as PropertyDeclaration;
    });
    // A declaration this path CREATES has to carry the same shape a style
    // would have given it. `inherits` matters most: the default is to inherit,
    // and resolution then walks PAST this declaration to an ancestor — so on a
    // themed scene with a provider between the object and the root, the pick
    // is written to disk and never rendered. `declarationForValue` also
    // supplies `kind`, which an asset picker needs to know what it holds. It
    // declines names outside the vocabulary (the manual bind button can point
    // a slot at any name), and those fall back to the requested shape.
    // The fallback is for names the vocabulary declines ENTIRELY — the manual
    // bind button can point a slot at any name. A name the vocabulary knows
    // but shapes differently is the NOT_AUTHORABLE case, not a licence to
    // redefine it: minting a string declaration holding a GLB URL under a
    // colour-typed `$structureColor` makes every sibling bound to that name
    // render the URL.
    const shaped = declarationForValue(name, value);
    const created: PropertyDeclaration = shaped
        ?? { name, type, value, inherits: THEME_DECLARATION_INHERITS } as PropertyDeclaration;

    // Declared under this name, but of a type this value cannot be written
    // into — a real conflict, not the sheet case. Silently dropping an inline
    // declaration would destroy authored data, and shadowing a sheet's or an
    // ancestor's with a local one of a different type is the same statement in
    // a quieter voice: a `$structureColor` the theme declares as a number is
    // not something a colour pick gets to redefine. Both say NOT_AUTHORABLE.
    // The vocabulary's shape for this name, read DIRECTLY — not inferred from
    // whether `declarationForValue` managed to build one. That returns null
    // precisely when the value does not fit the shape, so asking it is asking
    // the wrong question: a number-shaped name receiving a string produced
    // null, the guard saw nothing to conflict with, and the write minted a
    // string declaration under a numeric name. The string/colour pair only
    // seemed to work because any string satisfies both.
    const vocabulary = THEME_PROPERTY_SHAPES.get(name);
    const vocabularyDecl = vocabulary ? { name, ...vocabulary } as PropertyDeclaration : undefined;
    const conflicting = existing
        ?? findPropertyProvider(sceneObject, name)?.properties.getProperty(name)
        // Nothing declares it yet, but the vocabulary does — and disagrees
        // with what this write holds.
        ?? (vocabularyDecl && !authorable(vocabularyDecl) ? vocabularyDecl : undefined);
    if (conflicting && !authorable(conflicting)) {
        if (host.attach) await host.attach.undo({ silent: true });
        return {
            errors: [{
                message: `Property "${name}" is declared as ${conflicting.type} and cannot hold this value`,
                code: 'NOT_AUTHORABLE',
            }],
            target: null, propertyName: name, command: null,
        };
    }

    // Creating a declaration makes the property state-swappable, so states
    // authored BEFORE it would otherwise adopt this new base value
    // retroactively — the same trap `bindMaterialToProperty` freezes against.
    // Only on create: updating an existing declaration is an edit to a value
    // states already have their own overrides against.
    //
    // The freeze is seeded with the PRE-EDIT value, not the one just picked.
    // `created.value` is the new pick, so freezing on it pins every
    // override-less state to the new value — the retroactive adoption the
    // freeze exists to prevent, made permanent. Both `bind*` analogs seed
    // their declaration from the current resolved value and bind afterwards;
    // this path declares and writes in one go, so it seeds, freezes, then
    // sets the pick as the declaration's own value.
    const seed = preEdit !== undefined && valueMatchesType(created.type, preEdit) ? preEdit : value;
    const frozen = existing
        ? { properties: replaced, states: before.states }
        : setDeclarationValue(
            freezeStatesForDeclaration(before, { ...created, value: seed } as PropertyDeclaration),
            name,
            value,
        );

    const result = await commitDeclarations(
        host.owner, host.properties, name, before, frozen.properties,
        host.attach ? false : record, !!host.attach, frozen.states,
    );
    if (!host.attach) return result;

    // The attach already mutated the scene: fold it into one undoable entry, or
    // unwind it — a failed commit must not strand an empty Properties behavior.
    if (result.errors.length > 0 || !result.command) {
        await host.attach.undo({ silent: true });
        return result;
    }
    const command = new CompositeCommand(label, [host.attach, result.command]);
    if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    return { ...result, command };
}

/**
 * Set a material slot's PBR roughness or metallic factor, or the slot's own
 * opacity, WITHOUT destroying a property binding — the numeric analog of
 * {@link applyMaterialColor}. Routes:
 *
 * - slot bound + non-Base state active → the active state's override
 * - slot bound + Base                  → the providing declaration's base value
 * - slot bound, declaration missing    → declares it on the EDITED OBJECT's
 *   own host, with `inherits: false` (see {@link ownBindingHost}); an ancestor
 *   walk here is what made one object's pick repaint its siblings
 * - slot unbound                       → the Renderer's authored factor
 *
 * `value` is clamped to [0,1]; both factors are normalized PBR inputs and an
 * out-of-range write silently produces a material Babylon renders unpredictably.
 * Editors must call this instead of committing `materials` directly — a literal
 * write clobbers a binding and disconnects every state override.
 */
export async function applyMaterialFactor(
    sceneObject: SceneObject,
    factor: Factor,
    value: number,
    options: { slot?: number } & BindCommandOptions = {},
): Promise<ApplyMaterialSrcResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return {
            errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }],
            target: null, propertyName: null, command: null,
        };
    }
    if (!Number.isFinite(value)) {
        return {
            errors: [{ message: `${factor} factor must be a finite number`, code: 'INVALID_VALUE' }],
            target: null, propertyName: null, command: null,
        };
    }

    const { field } = FACTORS[factor];
    const clamped = unit(value);
    const slot = options.slot ?? 0;
    const record = options.record !== false;
    const authored = (renderer.getData().materials ?? [])
        .find(m => (m.targetMaterialIndex ?? 0) === slot)?.[field];
    const refMatch = typeof authored === 'string' ? (authored as string).match(WHOLE_REF_RE) : null;

    // ── Unbound slot: direct write to the Renderer ──
    if (!refMatch) {
        const before = renderer.getData();
        const materials: MaterialData[] = (before.materials ?? []).map(m => ({ ...m }));
        let entry = materials.find(m => (m.targetMaterialIndex ?? 0) === slot);
        if (!entry) {
            entry = { targetMaterialIndex: slot };
            materials.push(entry);
        }
        entry[field] = clamped;
        const errors = await renderer.commit({ materials });
        if (errors.length > 0) return { errors, target: null, propertyName: null, command: null };

        // fieldKey enables coalescing — sliders stream events on drag.
        const command = new BehaviorCommitCommand(
            sceneObject, 'Renderer', before, renderer.getData(), `materials[${slot}].${field}`,
        );
        if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
        dispatchBehaviorChanged({
            objectPath: sceneObject.getPath(), behaviorType: 'Renderer', action: 'updated',
        });
        return { errors: [], target: 'renderer', propertyName: null, command };
    }

    // ── Bound slot: route into the active state, else the declaration base ──
    const name = wholeRefName(refMatch);

    if (editTargetState(sceneObject) !== undefined) {
        const { captured, command } = await captureStateValue(sceneObject, name, clamped, { record });
        return { errors: [], target: captured ? 'state' : null, propertyName: name, command };
    }

    // Same three rungs as src/color, and the same reason for the last one: a
    // style leaves the slot bound while its value lives in a loaded sheet, so a
    // lookup that only reads inline declarations bails NOT_AUTHORABLE on every
    // later edit. Roughness/metallic are bound by adoption alongside src/color
    // (all four ROLE_SUFFIXES), so they get stuck exactly the same way.
    return authorBoundValue(
        sceneObject, name, 'number', clamped, record, `Set ${factor} on ${sceneObject.name}`,
    );
}

export interface SetMaterialForStateResult extends ApplyMaterialSrcResult {
    /** True when this call auto-promoted the slot to a state-swappable binding. */
    autoBound: boolean;
}

/** What a state edit of one Renderer field needs that differs between fields; the flow around it is shared. */
interface StateField {
    /** What a refusal calls the edit: `a color`, `a roughness`. */
    edit: string;
    /**
     * Whether a host that passes no `pageEditing` port is refused into the state the showing
     * page assigns. A slot's roughness, metallic and opacity always were; the colour, the
     * material and the whole opacity were written into that state, and a host that passes no
     * port still gets that, so the refusal is theirs only once they pass it.
     */
    portRequired: boolean;
    /** The undo label's noun. */
    label: string;
    /** The Renderer field's binding, and the name its property is declared under. */
    target: RendererBindingTarget;
    property: string;
    /** The field as authored: a literal, a `$name` binding string, or undefined. */
    authored: () => unknown;
    /** What the field shows at Base, as the declaration's first value. */
    seed: () => Promise<PropertyValue>;
    /** The value the state takes. */
    value: PropertyValue;
    /** The write at Base, which keeps a binding. */
    base: (record: boolean) => Promise<ApplyMaterialSrcResult>;
}

function slotEntryField(sceneObject: SceneObject, slot: number, field: keyof MaterialData): unknown {
    return sceneObject.getBehavior(Renderer)?.getData().materials?.find(m => (m.targetMaterialIndex ?? 0) === slot)?.[field];
}

/** The slot's current base color, to seed a colour property: its literal, else the detected albedo, else white. */
function colorSeed(sceneObject: SceneObject, slot: number, fallback?: string): string {
    const authored = slotEntryField(sceneObject, slot, 'baseColorFactor');
    let seed: string | undefined = typeof authored === 'string' && !isPropertyBinding(authored) ? authored : fallback;
    if (!isHexColor(seed)) {
        // Detect the live albedo. Headless hosts throw here, so degrade gracefully.
        try {
            const detected = MaterialPreviews.detectMaterialSlots(sceneObject)
                .find(s => (s.materialIndex ?? 0) === slot)?.albedoColor;
            if (isHexColor(detected)) seed = detected;
        } catch (error) {
            Logger.warn(`[bindMaterialColor] slot detection failed on ${sceneObject.name}`, error);
        }
    }
    return isHexColor(seed) ? seed : '#ffffffff';
}

/**
 * What the slot's `factor` shows at Base: its literal, else, for opacity, 1,
 * else the Base material's own factor times the mean of its metallic-roughness
 * texture's channel. A factor that is bound replaces that channel, so this is
 * the one number that leaves Default as it looks when the channel is even.
 * Known limit: a texture that never loads seeds the bare factor, which turns
 * Default metal when the texture's metallic channel is low.
 */
async function shownFactor(sceneObject: SceneObject, renderer: Renderer, factor: Factor, slot: number): Promise<number> {
    const authored = slotEntryField(sceneObject, slot, FACTORS[factor].field);
    if (typeof authored === 'number') return unit(authored);
    if (factor === 'opacity') return 1;
    const material = renderer.getBaseSlotMaterial(slot);
    if (!material) return 1;
    return (await materialOwnFinish(material))[factor] ?? unit(material[factor] ?? 1);
}

function slotField(sceneObject: SceneObject, slot: number, kind: 'src' | 'color' | Factor, value: string | number): StateField {
    if (kind === 'src') {
        return {
            edit: 'a material', portRequired: false, label: 'material', target: { binding: 'materialSrc', slot }, property: `material_${slot}`,
            authored: () => slotEntryField(sceneObject, slot, 'src'),
            seed: async () => {
                const src = slotEntryField(sceneObject, slot, 'src');
                return typeof src === 'string' && !isPropertyBinding(src) ? src : '';
            },
            value,
            base: record => applyMaterialSrc(sceneObject, String(value), { slot, record }),
        };
    }
    if (kind === 'color') {
        return {
            edit: 'a color', portRequired: false, label: 'colour', target: { binding: 'materialColor', slot }, property: `color_${slot}`,
            authored: () => slotEntryField(sceneObject, slot, 'baseColorFactor'),
            seed: async () => colorSeed(sceneObject, slot),
            value,
            base: record => applyMaterialColor(sceneObject, String(value), { slot, record }),
        };
    }
    const { field, binding } = FACTORS[kind];
    return {
        edit: kind === 'opacity' ? 'a slot opacity' : `a ${kind}`, portRequired: true, label: kind, target: { binding, slot }, property: `${kind}_${slot}`,
        authored: () => slotEntryField(sceneObject, slot, field),
        seed: () => shownFactor(sceneObject, sceneObject.getBehavior(Renderer)!, kind, slot),
        value: unit(Number(value)),
        base: record => applyMaterialFactor(sceneObject, kind, Number(value), { slot, record }),
    };
}

/**
 * The one state edit of a Renderer field, for every field: the value goes
 * where it is seen (see {@link routeOf}), the field is bound to a property
 * first when it isn't, and both land as one undo command. At Base it is the
 * field's own write, which keeps a binding.
 *
 * Every edit with a state or page showing is queued per object, in the order
 * it arrives, and asked again at its turn: with page editing off, an edit into
 * the state the showing page assigns is refused (`STATE_ROUTED`), as a pose or
 * visibility is, because that state may be other pages' too. A host that
 * passes no `pageEditing` port is refused only for a slot's roughness,
 * metallic and opacity; its colour, material and whole-opacity edits are
 * written into the showing state as they always were (`StateField.portRequired`).
 * Nothing is written, and the bind is taken back, when the route changes while
 * the bind awaits, or when the write doesn't land.
 */
async function setFieldForState(
    sceneObject: SceneObject,
    field: StateField,
    ports: { pageEditing?: PageEditing },
    record: boolean,
    usable = true,
): Promise<SetMaterialForStateResult> {
    const viaBase = async (): Promise<SetMaterialForStateResult> => ({ ...(await field.base(record)), autoBound: false });
    if (!sceneObject.getBehavior(Renderer) || !usable) return viaBase();
    const refused = refusedEdit(sceneObject, ports, field);
    if (refused) return refused;
    if (routeOf(sceneObject, ports, editTargetState).to === 'base') return viaBase();
    // The bind is part of the edit, so it is refused with it.
    if (stateEditRefused(sceneObject)) return nothingWritten();

    // One at a time per object: a tick of a slider drag after the first has bound must
    // not overtake the ones still queued before it.
    return queuePerObject(sceneObject, () => bindAndWrite(sceneObject, field, ports, record));
}

/** The refusal of an edit into a state the showing page assigns while page editing is off, else null. */
function refusedEdit(sceneObject: SceneObject, ports: { pageEditing?: PageEditing }, field: StateField): SetMaterialForStateResult | null {
    const refusalOf = field.portRequired ? pageStateRefusal : pageStateRefusalIfAsked;
    const refusal = refusalOf(sceneObject, ports, editTargetState, field.edit);
    return refusal ? nothingWritten([{ message: refusal.message, code: refusal.code }]) : null;
}

/** Shared with `text-bindings.ts`; not part of the public barrel. */
export function sameRoute(a: Route, b: Route): boolean {
    if (a.to === 'state') return b.to === 'state' && a.state === b.state;
    if (a.to === 'page') {
        return b.to === 'page' && a.page.container === b.page.container
            && a.page.pageIndex === b.page.pageIndex && a.page.path === b.page.path;
    }
    return b.to === 'base';
}

/**
 * Bind the field when it isn't, and write its value where the edit is seen, as
 * one command. Nothing is written when the edit has become Base's by its turn:
 * it was made for a state or page the user has since left.
 */
async function bindAndWrite(
    sceneObject: SceneObject,
    field: StateField,
    ports: { pageEditing?: PageEditing },
    record: boolean,
): Promise<SetMaterialForStateResult> {
    // Asked again at its turn: page editing may have gone off while this waited in the queue.
    const refused = refusedEdit(sceneObject, ports, field);
    if (refused) return refused;
    const seen = routeOf(sceneObject, ports, editTargetState);
    if (seen.to === 'base') return nothingWritten();
    const commands: UndoCommand[] = [];
    const takeBack = async (): Promise<void> => {
        for (const command of commands.reverse()) await command.undo({ silent: true });
    };

    let property = boundPropertyName(field.authored());
    if (property === null) {
        const seed = await field.seed();
        if (sceneObject.transform.isDisposed() || !sameRoute(routeOf(sceneObject, ports, editTargetState), seen)) return nothingWritten();
        const bind = await declareAndBind(
            sceneObject,
            bindingDeclaration(field.target.binding, field.property, seed),
            field.target,
            { label: `Bind ${field.label} to property on ${sceneObject.name}`, record: false },
        );
        if (!bind.command || bind.propertyName === null) return nothingWritten(bind.errors);
        commands.push(bind.command);
        property = bind.propertyName;
    }

    // The bind awaited its commits; the state that takes the value is the one shown now.
    if (!sameRoute(routeOf(sceneObject, ports, editTargetState), seen)) {
        await takeBack();
        return nothingWritten();
    }
    const name = property;
    const value = field.value;
    const autoBound = commands.length > 0;
    let write: UndoCommand | null;
    try {
        write = seen.to === 'page'
            ? await capturePageState(seen.page, `Set ${field.label} on ${seen.page.pageName}`, state => ({ ...state, values: { ...state.values, [name]: value } }))
            : (await captureStateValue(sceneObject, name, value, { record: false })).command;
    } catch (error) {
        await takeBack();
        return nothingWritten([{ message: error instanceof Error ? error.message : String(error), code: 'NOT_AUTHORABLE' }]);
    }
    if (!write) {
        await takeBack();
        return nothingWritten();
    }
    commands.push(write);
    const command = commands.length === 1
        ? write
        : new CompositeCommand(`Set ${field.label} for state on ${sceneObject.name}`, commands);
    if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    return { errors: [], target: 'state', propertyName: name, command, autoBound };
}

/**
 * `pageEditing` is the host's page-editing port (see `pageCaptureContext`). Passed, shared
 * content of a Pages container goes to the showing page's state while it says editing is
 * on, and is refused (`STATE_ROUTED`) into the state the page assigns while it says off.
 * Omitted, a colour or material edit is written into the showing state, as before the port.
 */
type StateEditOptions = { slot?: number; pageEditing?: PageEditing } & BindCommandOptions;

/**
 * Transparent "set this slot's material src for the current state context" —
 * the high-level entry point for editors where binding is invisible to the user.
 * With a state or page showing, the slot is bound first when it isn't and the
 * value becomes that state's (one undo entry); at Base it routes via
 * {@link applyMaterialSrc}. See {@link setFieldForState}.
 */
export function setMaterialSrcForState(
    sceneObject: SceneObject,
    src: string,
    options: StateEditOptions = {},
): Promise<SetMaterialForStateResult> {
    const field = slotField(sceneObject, options.slot ?? 0, 'src', src);
    return setFieldForState(sceneObject, field, { pageEditing: options.pageEditing }, options.record !== false);
}

/**
 * Transparent "set this slot's base color for the current state context".
 * Color analog of {@link setMaterialSrcForState}. `hex` is `#rrggbb[aa]`.
 */
export function setMaterialColorForState(
    sceneObject: SceneObject,
    hex: string,
    options: StateEditOptions = {},
): Promise<SetMaterialForStateResult> {
    const field = slotField(sceneObject, options.slot ?? 0, 'color', hex);
    return setFieldForState(sceneObject, field, { pageEditing: options.pageEditing }, options.record !== false);
}

/**
 * Transparent "set this slot's roughness, metallic or own opacity for the
 * current state context", the numeric analog of {@link setMaterialColorForState}.
 * The value goes where it is seen: the state that shows on the object or its
 * group, or, for shared content of a Pages container while `pageEditing` says
 * page editing is on, the showing page's state. See {@link setFieldForState}.
 *
 * The property starts as what the slot shows at Base ({@link shownFactor}), so
 * Default keeps its look when the texture's channel is even; where it varies,
 * Default takes the channel's mean, because a bound factor replaces the channel.
 */
export function setMaterialFactorForState(
    sceneObject: SceneObject,
    factor: Factor,
    value: number,
    options: StateEditOptions = {},
): Promise<SetMaterialForStateResult> {
    const field = slotField(sceneObject, options.slot ?? 0, factor, value);
    return setFieldForState(sceneObject, field, { pageEditing: options.pageEditing }, options.record !== false, Number.isFinite(value));
}

/**
 * Set the Renderer's whole-object opacity without clobbering a binding. `value`
 * is in [0, 1]. At Base: a bound opacity takes it as the property's base value
 * (declared on the object itself when nothing authorable declares it), an
 * unbound one as the Renderer's literal. With a state or page showing: see
 * {@link setFieldForState}; the property is seeded with the opacity Base shows.
 *
 * The object must have a Renderer. `record: false` returns the one command.
 */
export function setOpacityForState(
    sceneObject: SceneObject,
    value: number,
    options: { pageEditing?: PageEditing } & BindCommandOptions = {},
): Promise<SetMaterialForStateResult> {
    const field: StateField = {
        edit: 'an opacity', portRequired: false, label: 'opacity', target: { binding: 'opacity' }, property: 'opacity',
        authored: () => sceneObject.getBehavior(Renderer)?.getData().opacity,
        seed: async () => {
            const authored: unknown = sceneObject.getBehavior(Renderer)?.getData().opacity;
            return typeof authored === 'number' ? authored : 1;
        },
        value,
        base: record => applyOpacity(sceneObject, value, record),
    };
    return setFieldForState(sceneObject, field, { pageEditing: options.pageEditing }, options.record !== false);
}

/** Write the Renderer's whole-object opacity at Base: through its binding when it has one, else as a literal. */
async function applyOpacity(sceneObject: SceneObject, value: number, record: boolean): Promise<ApplyMaterialSrcResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return { errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }], target: null, propertyName: null, command: null };
    }
    const authored: unknown = renderer.getData().opacity;
    const ref = typeof authored === 'string' ? authored.match(WHOLE_REF_RE) : null;
    if (ref) {
        const name = wholeRefName(ref);
        if (editTargetState(sceneObject) !== undefined) {
            const { captured, command } = await captureStateValue(sceneObject, name, value, { record });
            return { errors: [], target: captured ? 'state' : null, propertyName: name, command };
        }
        return authorBoundValue(sceneObject, name, 'number', value, record, `Set opacity on ${sceneObject.name}`);
    }
    const before = renderer.getData();
    const errors = await renderer.commit({ opacity: value });
    if (errors.length > 0) return { errors, target: null, propertyName: null, command: null };
    const command = new BehaviorCommitCommand(sceneObject, 'Renderer', before, renderer.getData(), 'opacity');
    if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    dispatchBehaviorChanged({ objectPath: sceneObject.getPath(), behaviorType: 'Renderer', action: 'updated' });
    return { errors: [], target: 'renderer', propertyName: null, command };
}

// ── Binding introspection + unbind (for the color-binding selector) ─────────

export interface BindableColorProperty {
    /** Declaration name — the `$name` a slot binds to. */
    name: string;
    /** Display label (declaration `label` ?? name). */
    label: string;
    /** Object whose Properties behavior declares it (self or an ancestor). */
    provider: SceneObject;
    /** Whether the declaration already carries variants. */
    hasVariants: boolean;
}

/**
 * List the color-typed Properties declarations reachable from `sceneObject`
 * (its own Properties behavior plus ancestors) — the candidates a material
 * slot's `baseColorFactor` can bind to. Nearer providers first; de-duplicated
 * by name (a nearer declaration shadows a same-named ancestor one, matching the
 * provider walk). Read-only.
 */
export function listBindableColorProperties(sceneObject: SceneObject): BindableColorProperty[] {
    const out: BindableColorProperty[] = [];
    const seen = new Set<string>();
    let owner: SceneObject | null = sceneObject;
    while (owner) {
        const props = owner.getBehavior(Properties);
        for (const d of props?.getData().properties ?? []) {
            // A same-named declaration of another type at a nearer level shadows
            // the colour further up — the provider walk is type-blind.
            if (seen.has(d.name)) continue;
            seen.add(d.name);
            if (d.type !== 'color') continue;
            out.push({
                name: d.name,
                label: d.label ?? d.name,
                provider: owner,
                hasVariants: Array.isArray(d.variants) && d.variants.length > 0,
            });
        }
        owner = owner.getParent();
    }
    return out;
}

export interface UnbindMaterialColorResult {
    /** Empty on success. */
    errors: ValidationError[];
    /** Undo command for the write (null on failure / no-op). */
    command: UndoCommand | null;
}

/**
 * Unbind a slot's `baseColorFactor`: replace the `$name` binding with the
 * property's CURRENT resolved color as a literal, so the visible color is
 * unchanged but the slot no longer follows the property. The inverse of
 * {@link bindMaterialColorToProperty} at the Renderer layer — it does NOT
 * delete the declaration (other slots may still bind it). No-op success when
 * the slot isn't bound.
 */
export async function unbindMaterialColor(
    sceneObject: SceneObject,
    options: { slot?: number } & BindCommandOptions = {},
): Promise<UnbindMaterialColorResult> {
    const renderer = sceneObject.getBehavior(Renderer);
    if (!renderer) {
        return { errors: [{ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' }], command: null };
    }

    const slot = options.slot ?? 0;
    const record = options.record !== false;
    const authoredColor = (renderer.getData().materials ?? [])
        .find(m => (m.targetMaterialIndex ?? 0) === slot)?.baseColorFactor;
    const refMatch = typeof authoredColor === 'string' ? authoredColor.match(WHOLE_REF_RE) : null;
    if (!refMatch) return { errors: [], command: null }; // already unbound

    const name = wholeRefName(refMatch);
    const provider = findPropertyProvider(sceneObject, name);
    const resolved = provider?.properties.getValue(name);
    // Fall back to white if the property resolves to a non-hex (shouldn't happen
    // for a color declaration, but never write a binding-looking literal).
    const literal = isHexColor(resolved) ? resolved : '#ffffff';

    const before = renderer.getData();
    const materials: MaterialData[] = (before.materials ?? []).map(m => ({ ...m }));
    const entry = materials.find(m => (m.targetMaterialIndex ?? 0) === slot);
    if (!entry) return { errors: [], command: null };
    entry.baseColorFactor = literal;

    const errors = await renderer.commit({ materials });
    if (errors.length > 0) return { errors, command: null };

    const command = new BehaviorCommitCommand(
        sceneObject, 'Renderer', before, renderer.getData(), null,
    );
    if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    dispatchBehaviorChanged({
        objectPath: sceneObject.getPath(), behaviorType: 'Renderer', action: 'updated',
    });
    return { errors: [], command };
}
